<template>
  <div class="h-full bg-[#f8fafc] p-4 overflow-y-auto custom-scroll">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] transition-all overflow-hidden mb-6">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Display</h2>
      </div>

      <div v-if="loading" class="px-6 pb-2 pt-0">
        <div class="text-xs text-[#0d4169] flex items-center gap-2 font-medium">
          <span class="w-4 h-4 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin"></span>
          Loading...
        </div>
      </div>

      <!-- Results Table Section -->
      <div class="relative overflow-x-auto min-h-[400px] px-3">
        <table class="display-orders-table w-full border-collapse bg-white">
          <thead class="sticky top-0 z-10 bg-[#f8fafc] shadow-sm">
            <tr>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">No</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Menu Name</th>
              <th class="px-4 py-3 text-center font-bold text-[#64748b] border-b text-[11px]">Quantity</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Exclude</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Note</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Waiter Name</th>
              <!-- <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Table No.</th> -->
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Action</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Status</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Order Time</th>
             
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Finished/Out Time</th>
             
      
            </tr>
          </thead>

          <tbody>
            <tr v-if="rows.length === 0">
              <td colspan="10" class="px-4 py-32 text-center text-slate-400 italic text-sm">
                <div class="text-5xl mb-4 opacity-20">📄</div>
                No orders to display.
              </td>
            </tr>

            <tr
              v-for="(row, index) in rows"
              :key="row._rowKey || row.id"
              :class="[
                'display-order-row border-b',
                { 'table-group-end': shouldShowTableGroupBottomBorder(index) }
              ]"
            >
              <td class="px-4 py-3 text-slate-500 text-xs">{{ index + 1 }}</td>

              <td class="px-4 py-3 text-left font-semibold text-[#0d4169] text-[11px]">
                {{ row.menu?.name || '---' }}
              </td>

              <td class="px-4 py-3 text-center font-bold text-slate-600 text-xs">
                {{ formatQuantity(row.quantity) }}
              </td>

              <td class="px-4 py-3 text-left text-slate-600 text-xs">{{ row.excludeIngredients || '' }}</td>
              <td class="px-4 py-3 text-left text-slate-600 text-xs">{{ row.note || '' }}</td>
              <td class="px-4 py-3 text-left text-slate-600 text-xs">
                <button
                  v-if="isGuestWaiter(row)"
                  @click="openWaiterSelectionModal(row)"
                  class="text-blue-600 hover:text-blue-700 hover:underline font-semibold"
                >
                  {{ waiterNameWithTableNumber(row) }}
                </button>
                <span v-else>{{ waiterNameWithTableNumber(row) }}</span>
              </td>
              <!-- <td class="px-4 py-3 text-left text-slate-600 text-xs">{{ row.tableNumber || '---' }}</td> -->
              <td class="px-4 py-3 text-left">
                <button
                  v-if="row.preparation_status === 'Pending' && canStart && !orderHasScannableIngredients(row)"
                  @click="handleAction(row)"
                  class="px-3 py-1 text-[11px] font-bold rounded-md shadow-sm transition-all active:scale-95 bg-[#0d4169] text-white hover:bg-[#0a3354]"
                >
                  Start
                </button>

                <button
                  v-else-if="row.preparation_status === 'Pending' && canStart && orderHasScannableIngredients(row)"
                  @click="openScanModal(row)"
                  class="px-3 py-1 text-[11px] font-bold rounded-md shadow-sm transition-all active:scale-95 bg-[#0d4169] text-white hover:bg-[#0a3354]"
                >
                  Scan
                </button>

                <button
                  v-else-if="row.preparation_status === 'Started' && canFinish"
                  @click="handleAction(row)"
                  class="px-3 py-1 text-[11px] font-bold rounded-md shadow-sm transition-all active:scale-95 bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Finish
                </button>

                <button
                  v-else-if="row.preparation_status === 'Finished' && canOut"
                  @click="handleAction(row)"
                  class="px-3 py-1 text-[11px] font-bold rounded-md shadow-sm transition-all active:scale-95 bg-red-600 text-white hover:bg-red-700"
                >
                  Out
                </button>

                <button
                  v-else
                  disabled
                  class="px-3 py-1 text-[11px] font-bold rounded-md shadow-sm bg-slate-200 text-slate-500 cursor-not-allowed"
                >
                  {{ row.preparation_status === 'Out' ? (row.outed_by_user_name || 'Out') : '---' }}
                </button>
              </td>
              <td
                class="px-4 py-3 text-left font-bold text-xs"
                :class="statusClass(row.preparation_status)"
              >
                {{ row.preparation_status || '---' }}
              </td>
              <td class="px-4 py-3 text-left text-slate-600 text-xs">{{ formatOrderTime(row.createdAt) }}</td>

            

              <td class="px-4 py-3 text-left text-slate-600 text-xs">
                {{ formatFinishedOutTime(row) }}
              </td>

            

              
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Transition name="toast">
      <div v-if="toast.show" class="fixed top-4 right-4 z-[1000] max-w-sm">
        <div
          :class="[
            'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[300px]',
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          ]"
        >
          <span v-if="toast.type === 'success'">✅</span>
          <span v-else>❌</span>
          <span class="font-medium">{{ toast.message }}</span>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <div
        v-if="showScanModal"
        class="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
        @click.self="closeScanModal"
      >
        <div class="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
          <div class="flex items-center justify-between bg-[#0d4169] px-4 py-3">
            <h3 class="text-sm font-bold text-white">Scan Items</h3>
            <button
              type="button"
              aria-label="Close scan modal"
              class="flex h-6 w-6 items-center justify-center rounded-full text-lg text-white transition-all hover:bg-white/10"
              @click="closeScanModal"
            >
              &times;
            </button>
          </div>
          <div class="space-y-4 p-4">
            <label class="block">
              <span class="mb-1 block text-xs font-semibold text-slate-700">Scan item</span>
              <input
                ref="scanInput"
                v-model="scanValue"
                type="text"
                autocomplete="off"
                class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#0d4169] focus:ring-1 focus:ring-[#0d4169]"
                placeholder="Scan barcode"
                :disabled="isBarcodeValidationInProgress || allScannedItemsComplete"
                @keydown.enter.prevent="scanValue.trim() && validateAndRecordBarcode(scanValue.trim())"
              >
            </label>
            <div class="max-h-64 space-y-2 overflow-y-auto">
              <div
                v-for="(item, index) in scanModalItems"
                :key="`${item.itemId}-${index}`"
                class="rounded-md border border-slate-200 px-3 py-2"
              >
                <div class="flex items-center justify-between gap-2">
                  <p class="min-w-0 truncate text-sm font-semibold text-slate-800">
                    Scan {{ item.itemName }} {{ formatQuantity(item.quantity) }} times
                  </p>
                  <button
                    type="button"
                    class="shrink-0 rounded bg-[#0d4169] px-2 py-1 text-[10px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-400"
                    :disabled="isScanItemComplete(item) || isBarcodeValidationInProgress"
                    @click="openHostedBarcodeScanner(item)"
                  >
                    Scan
                  </button>
                </div>
                <div
                  v-for="scanNumber in getScanQuantityRows(item.quantity)"
                  :key="scanNumber"
                  class="mt-1 flex items-center gap-2 text-xs"
                  :class="isScanRowComplete(item, scanNumber) ? 'text-green-700' : 'text-slate-600'"
                >
                  <span>{{ scanNumber }}</span>
                  <svg
                    v-if="isScanRowComplete(item, scanNumber)"
                    viewBox="0 0 20 20"
                    class="h-4 w-4"
                    fill="currentColor"
                    aria-label="Scanned"
                    role="img"
                  >
                    <path fill-rule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.2 7.26a1 1 0 0 1-1.42.006l-3.8-3.74a1 1 0 1 1 1.404-1.426l3.09 3.04 6.494-6.548a1 1 0 0 1 1.426-.006Z" clip-rule="evenodd" />
                  </svg>
                  <span v-else>?</span>
                </div>
              </div>
              <p v-if="scanModalItems.length === 0" class="text-sm text-slate-500">
                No scannable items found for this order.
              </p>
            </div>
          </div>
          <div class="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-3">
            <button
              type="button"
              class="rounded-md bg-slate-200 px-4 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isCompletingScannedOrderOut"
              @click="closeScanModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="w-32 rounded-md bg-red-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-400"
              :disabled="!allScannedItemsComplete || isBarcodeValidationInProgress || isCompletingScannedOrderOut || !canOut"
              @click="outScannedOrder"
            >
              {{ isCompletingScannedOrderOut ? 'Moving Out...' : 'Out' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <div
        v-if="showWaiterSelectionModal"
        class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
          <div class="px-4 py-3 flex justify-between items-center bg-[#0d4169]">
            <h3 class="text-sm font-bold text-white capitalize tracking-wide">Select Waiter</h3>
            <button
              @click="closeWaiterSelectionModal"
              class="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 text-white transition-all text-lg"
            >
              &times;
            </button>
          </div>

          <div class="p-4">
            <select
              v-model="selectedWaiterForGuest"
              class="w-full text-sm border border-gray-300 rounded-sm py-2 px-3 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
            >
              <option value="" disabled>Select Waiter</option>
              <option v-for="user in activeUsers" :key="user.id" :value="user.id">
                {{ user.full_name || user.username }}
              </option>
            </select>
          </div>

          <div class="bg-gray-50 px-4 py-3 flex justify-end border-t border-gray-100">
            <button
              @click="saveGuestWaiterAssignment"
              class="px-4 py-1.5 bg-[#0d4169] text-white rounded text-xs font-bold hover:bg-[#0a3354] transition-all"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const currentUser = computed(() => store.getters.currentUser)
const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return {}
  }
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
function getPrivilegeBlock(name) {
  return userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null
}
const displayPrivilege = computed(() => getPrivilegeBlock('Display') || {})
// For POS Display, Start/Finish/Out are stored as View/Void/Approve.
const canStart = computed(() => hasAdminPrivilege.value || !!displayPrivilege.value?.View)
const canFinish = computed(() => hasAdminPrivilege.value || !!displayPrivilege.value?.Void)
const canOut = computed(() => hasAdminPrivilege.value || !!displayPrivilege.value?.Approve)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const userDepartmentIds = computed(() => {
  const ids = new Set()
  // Add primary department ID
  if (currentUser.value?.departmentId) ids.add(String(currentUser.value.departmentId))
  if (currentUser.value?.department_id) ids.add(String(currentUser.value.department_id))
  if (currentUser.value?.department?.id) ids.add(String(currentUser.value.department.id))
  
  // Resolve additional department NAMES into numeric IDs
  const additionalDeptsStr = currentUser.value?.departments
  if (typeof additionalDeptsStr === 'string' && additionalDeptsStr.trim()) {
    const names = additionalDeptsStr.split(',').map(n => n.trim().toLowerCase()).filter(Boolean)
    
    // Find IDs corresponding to names in allDepartments
    names.forEach(name => {
      const match = allDepartments.value.find(d => d.name.toLowerCase() === name)
      if (match) ids.add(String(match.id))
    })
  }
  return ids
})

const loading = ref(false)
const loadingCount = ref(0)
const orders = ref([])
const users = ref([])
const allDepartments = ref([])
const departmentsLoaded = ref(false)
const scannableIngredientsLoaded = ref(false)
const toast = ref({ show: false, message: '', type: 'success' })
const showWaiterSelectionModal = ref(false)
const showScanModal = ref(false)
const scanModalOrder = ref(null)
const scanValue = ref('')
const scanInput = ref(null)
const scannedBarcodes = ref({})
const isBarcodeValidationInProgress = ref(false)
const isCompletingScannedOrderOut = ref(false)
let scanModalSessionId = 0
let hostedBarcodeScannerWindow = null
let activeBarcodeScanKey = null
const HOSTED_BARCODE_SCANNER_ORIGIN = 'https://theminasoft.com'
const selectedGuestRow = ref(null)
const selectedWaiterForGuest = ref('')

// Speech Alerts Logic
const speakingNow = ref(false)
const announcedOrderIds = ref(new Set())
const LS_DISPLAY_ANNOUNCED_IDS = 'displayPage_announcedOrderIds'
const MAX_STORED_IDS = 800
const speechVoices = ref([])
const selectedSpeechVoice = ref(null)

function isAndroidSpeechClient() {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent || '')
}

function isSpeechSupported() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return false
  const ua = navigator.userAgent || ''
  const pl = navigator.platform || ''
  if (/iPhone|iPod|iPad/i.test(ua)) return false
  if (pl === 'MacIntel' && navigator.maxTouchPoints > 1) return false
  return /Android|Windows/i.test(ua) || /Win32|Win64|Windows/i.test(pl)
}

function loadAnnouncedIds() {
  try {
    const raw = localStorage.getItem(LS_DISPLAY_ANNOUNCED_IDS)
    const arr = raw ? JSON.parse(raw) : []
    announcedOrderIds.value = new Set(Array.isArray(arr) ? arr.map(String) : [])
  } catch {
    announcedOrderIds.value = new Set()
  }
}

function saveAnnouncedIds() {
  const arr = [...announcedOrderIds.value].slice(-MAX_STORED_IDS)
  localStorage.setItem(LS_DISPLAY_ANNOUNCED_IDS, JSON.stringify(arr))
}

function pickPreferredEnglishVoice(voices) {
  if (!Array.isArray(voices) || voices.length === 0) return null
  const normalized = voices.map((v) => ({
    raw: v,
    lang: String(v?.lang || '').replace(/_/g, '-').toLowerCase(),
    name: String(v?.name || '').toLowerCase()
  }))
  const langRank = (lang) => {
    if (lang === 'en-us') return 100
    if (lang.startsWith('en-us')) return 90
    if (lang === 'en-gb') return 80
    if (lang.startsWith('en-gb')) return 70
    if (lang.startsWith('en')) return 50
    return -1
  }
  let best = null
  let bestRank = -1
  for (const row of normalized) {
    const lr = langRank(row.lang)
    if (lr < 0) continue
    let r = lr
    const raw = row.raw
    if (raw && typeof raw.localService === 'boolean') {
      if (raw.localService) r += 35
      else r -= 6
    }
    if (r > bestRank) {
      bestRank = r
      best = raw
    }
  }
  return best
}

function pickPreferredFemaleAfricanVoice(voices) {
  if (!Array.isArray(voices) || voices.length === 0) return null
  const femaleHints = ['female', 'zira', 'aria', 'jenny', 'sara', 'samantha']
  const ethiopianLangPriority = ['am-et', 'en-et', 'om-et', 'ti-et']
  const africanLangPriority = ['sw-ke', 'sw-tz', 'en-ke', 'en-za', 'af-za']
  const normalized = voices.map(v => ({
    raw: v,
    name: String(v?.name || '').toLowerCase(),
    lang: String(v?.lang || '').toLowerCase()
  }))
  const findFemaleByLangPriority = (langPriority) => {
    for (const langCode of langPriority) {
      const match = normalized.find(v => v.lang === langCode && femaleHints.some(h => v.name.includes(h)))
      if (match) return match.raw
    }
    return null
  }
  const findAnyByLangPriority = (langPriority) => {
    for (const langCode of langPriority) {
      const match = normalized.find(v => v.lang === langCode)
      if (match) return match.raw
    }
    return null
  }
  return (
    findFemaleByLangPriority(ethiopianLangPriority) ||
    findAnyByLangPriority(ethiopianLangPriority) ||
    findFemaleByLangPriority(africanLangPriority) ||
    findAnyByLangPriority(africanLangPriority) ||
    normalized.find(v => v.lang.startsWith('en') && femaleHints.some(h => v.name.includes(h)))?.raw ||
    voices[0]
  )
}

function loadSpeechVoices() {
  if (!isSpeechSupported()) return
  if (!('speechSynthesis' in window)) return
  speechVoices.value = window.speechSynthesis.getVoices() || []
  selectedSpeechVoice.value = isAndroidSpeechClient()
    ? pickPreferredEnglishVoice(speechVoices.value)
    : pickPreferredFemaleAfricanVoice(speechVoices.value)
}

function speak(text) {
  if (!isSpeechSupported() || speakingNow.value) return
  
  const synth = window.speechSynthesis
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = selectedSpeechVoice.value?.lang || 'en-US'
  utterance.volume = 1
  utterance.rate = isAndroidSpeechClient() ? 1 : 1.15
  utterance.pitch = 1
  
  if (selectedSpeechVoice.value) {
    utterance.voice = selectedSpeechVoice.value
  }
  
  speakingNow.value = true
  
  utterance.onend = () => { speakingNow.value = false }
  utterance.onerror = () => { speakingNow.value = false }
  
  // Watchdog to reset state if TTS gets stuck
  setTimeout(() => {
    if (speakingNow.value) speakingNow.value = false
  }, 6000)

  synth.speak(utterance)
}

function unlockSpeech() {
  if (!isSpeechSupported()) return
  const synth = window.speechSynthesis
  try {
    const u = new SpeechSynthesisUtterance('')
    u.volume = 0
    synth.speak(u)
  } catch (_) {
    /* ignore */
  }
}

const activeUsers = computed(() => {
  return (Array.isArray(users.value) ? users.value : [])
    .filter(u => Number(u?.status) === 1)
    .sort((a, b) => {
      const nameA = String(a?.full_name || a?.username || '').trim().toLowerCase()
      const nameB = String(b?.full_name || b?.username || '').trim().toLowerCase()
      return nameA.localeCompare(nameB)
    })
})

function formatQuantity(qty) {
  if (qty === null || qty === undefined) return '---'
  const n = Number(qty)
  if (Number.isNaN(n)) return String(qty)
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n))
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

function formatOrderTime(dateStr) {
  if (!dateStr) return '---'
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return '---'
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`
}

function formatHms(dateStr) {
  if (!dateStr) return '---'
  if (typeof dateStr === 'string') {
    // Keep backend clock value as-is (avoid timezone shifting in browser parsing).
    const timeMatch = dateStr.match(/(\d{2}):(\d{2}):(\d{2})/)
    if (timeMatch) return `${timeMatch[1]}:${timeMatch[2]}:${timeMatch[3]}`
  }
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return '---'
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${hh}:${min}:${ss}`
}

function formatFinishedOutTime(row) {
  if (!row) return '---'
  const finishDateValue = row.finish_date || row.finishDate || null
  const outDateValue = row.out_date || row.outDate || null
  if (row.preparation_status === 'Finished') {
    return formatHms(finishDateValue)
  }
  if (row.preparation_status === 'Out') {
    const finishTime = formatHms(finishDateValue)
    const outTime = formatHms(outDateValue)
    if (finishTime === '---' && outTime === '---') return '---'
    return `${finishTime} / ${outTime}`
  }
  return '---'
}

const STATUS_COUNT_KEYS = ['Pending', 'Started', 'Finished', 'Out']

function emptyStatusCount() {
  return { Pending: 0, Started: 0, Finished: 0, Out: 0 }
}

function normalizeStatusCount(raw, quantity, preparationStatus) {
  const qty = parseFloat(quantity) || 0
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const sc = emptyStatusCount()
    STATUS_COUNT_KEYS.forEach((k) => {
      const lower = k.toLowerCase()
      sc[k] = parseFloat(raw[k] ?? raw[lower] ?? 0) || 0
    })
    const sum = STATUS_COUNT_KEYS.reduce((s, k) => s + sc[k], 0)
    if (sum > 0) return sc
  }
  const status = String(preparationStatus || 'Pending')
  const sc = emptyStatusCount()
  if (STATUS_COUNT_KEYS.includes(status)) {
    sc[status] = qty
  } else {
    sc.Pending = qty
  }
  return sc
}

function expandOrderRows(order) {
  const sc = normalizeStatusCount(
    order.statusCount ?? order.status_count,
    order.quantity,
    order.preparation_status
  )
  const expanded = []
  STATUS_COUNT_KEYS.forEach((status) => {
    const count = sc[status] || 0
    if (count > 0) {
      expanded.push({
        ...order,
        preparation_status: status,
        quantity: count,
        _rowKey: `${order.id}-${status}`
      })
    }
  })
  return expanded
}

const rows = computed(() => {
  // Required: if user has no departments assigned, show nothing
  const allowedDepts = userDepartmentIds.value
  if (allowedDepts.size === 0) return []

  const allowedStatuses = new Set(['Pending', 'Started', 'Finished', 'Out'])

  const expandedRows = (Array.isArray(orders.value) ? orders.value : [])
    .filter(o => {
      const od = o?.menu?.departmentId ?? o?.menu?.department_id
      return allowedDepts.has(String(od))
    })
    .filter(o => {
      if (o.fromWhere === 'qr') return Number(o.isPaid) === 1
      return true
    })
    .flatMap(expandOrderRows)
    .filter(o => allowedStatuses.has(o.preparation_status))

  // Sort logic:
  // 1. Pending/Started/Finished items at top
  // 2. "Out" items at bottom
  // 3. Within same priority, order chronically (oldest first)
  return expandedRows.sort((a, b) => {
    const statusA = a.preparation_status
    const statusB = b.preparation_status

    if (statusA === 'Out' && statusB !== 'Out') return 1
    if (statusB === 'Out' && statusA !== 'Out') return -1

    const ta = new Date(a?.createdAt).getTime()
    const tb = new Date(b?.createdAt).getTime()
    const safeA = Number.isFinite(ta) ? ta : 0
    const safeB = Number.isFinite(tb) ? tb : 0
    return safeA - safeB
  })
})

function normalizeTableNumber(row) {
  if (!row) return ''
  return String(row.tableNumber ?? '').trim()
}

function shouldShowTableGroupBottomBorder(index) {
  const list = rows.value
  const currentRow = list[index]
  if (!currentRow || currentRow.preparation_status === 'Out') return false

  const currentTable = normalizeTableNumber(currentRow)
  const nextRow = list[index + 1]

  if (!nextRow) return true
  if (nextRow.preparation_status === 'Out') return true

  return normalizeTableNumber(nextRow) !== currentTable
}

function resolveWaiterName(row) {
  if (row?.guest_waiter_user_id) {
    return `Guest/${row?.guest_waiter_user_name || '---'}`
  }
  const waiterId = row?.waiterId
  if (!waiterId) return 'Guest'
  const id = String(waiterId)
  const u = users.value.find(x => String(x.id) === id)
  return u?.full_name || u?.username || 'Guest'
}

function normalizeTableNumberDigits(row) {
  const raw = row?.tableNumber ?? ''
  const s = String(raw).trim()
  const m = s.match(/\d+/)
  return m ? m[0] : ''
}

function waiterNameWithTableNumber(row) {
  const base = resolveWaiterName(row)
  const digits = normalizeTableNumberDigits(row)
  return digits ? `${base} (${digits})` : base
}

function isGuestWaiter(row) {
  const hasAssignedGuestWaiter = !!row?.guest_waiter_user_id
  return !row?.waiterId && !hasAssignedGuestWaiter
}

function openWaiterSelectionModal(row) {
  selectedGuestRow.value = row || null
  selectedWaiterForGuest.value = ''
  showWaiterSelectionModal.value = true
}

function closeWaiterSelectionModal() {
  showWaiterSelectionModal.value = false
  selectedGuestRow.value = null
  selectedWaiterForGuest.value = ''
}

async function saveGuestWaiterAssignment() {
  if (!selectedGuestRow.value?.id) {
    showToast('Selected order is invalid', 'error')
    return
  }
  if (!selectedWaiterForGuest.value) {
    showToast('Please select a waiter', 'error')
    return
  }

  const createdBy = Number(currentUser.value?.id || currentUser.value?.sub || 0)
  if (!createdBy) {
    showToast('Unauthorized user', 'error')
    return
  }

  const hasCompanyId = companyId.value !== null && companyId.value !== undefined && String(companyId.value) !== ''
  if (!hasCompanyId) {
    showToast('Company context is missing', 'error')
    return
  }

  try {
    const payload = {
      userId: Number(selectedWaiterForGuest.value),
      companyId: Number(companyId.value),
      branchId: branchId.value !== null && branchId.value !== undefined && String(branchId.value) !== ''
        ? Number(branchId.value)
        : null,
      orderId: Number(selectedGuestRow.value.id),
      createdAt: selectedGuestRow.value.createdAt || new Date().toISOString(),
      createdBy
    }

    const res = await apiFetch('/orders/guest-waiter', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    const data = await res.json().catch(() => ({}))

    if (!res.ok) {
      showToast(data.message || 'Failed to save guest waiter', 'error')
      return
    }

    const idx = orders.value.findIndex(o => Number(o?.id) === Number(selectedGuestRow.value?.id))
    if (idx !== -1) {
      const selectedUser = activeUsers.value.find(u => Number(u?.id) === Number(selectedWaiterForGuest.value))
      orders.value[idx].guest_waiter_user_id = Number(selectedWaiterForGuest.value)
      orders.value[idx].guest_waiter_user_name = selectedUser?.full_name || selectedUser?.username || null
    }

    showToast(data.message || 'Guest waiter saved successfully', 'success')
    closeWaiterSelectionModal()
  } catch (error) {
    console.error('Failed to save guest waiter assignment:', error)
    showToast('An error occurred while saving guest waiter', 'error')
  }
}

function orderHasScannableIngredients(order) {
  if (!scannableIngredientsLoaded.value || !order?._scannableIngredientsKnown) return true
  const ingredients = order?.ingredient_and_amount ?? order?.ingredientAndAmount
  return Array.isArray(ingredients) && ingredients.some((ingredient) => (
    ingredient?.is_scannable === true || Number(ingredient?.is_scannable) === 1
  ))
}

const scanModalItems = computed(() => {
  const order = scanModalOrder.value
  const ingredients = order?.ingredient_and_amount ?? order?.ingredientAndAmount
  if (!Array.isArray(ingredients)) return []

  return ingredients
    .filter((ingredient) => ingredient?.is_scannable === true || Number(ingredient?.is_scannable) === 1)
    .map((ingredient, index) => ({
      itemId: ingredient.Id ?? ingredient.itemId ?? ingredient.id,
      itemName: ingredient.itemName ?? ingredient.item_name ?? ingredient.name ?? 'Item',
      quantity: (Number(order?.quantity) || 0) * (Number(ingredient.amount) || 0),
      scanKey: `${ingredient.Id ?? ingredient.itemId ?? ingredient.id ?? 'item'}-${index}`,
    }))
})

const allScannedItemsComplete = computed(() => (
  scanModalItems.value.length > 0
  && scanModalItems.value.every((item) => isScanItemComplete(item))
))

function getScanQuantityRows(quantity) {
  const count = Number(quantity)
  if (!Number.isFinite(count) || count <= 0) return []
  return Array.from({ length: Math.ceil(count) }, (_, index) => index + 1)
}

function getScannedBarcodes(scanKey) {
  const scanned = scannedBarcodes.value[scanKey]
  return Array.isArray(scanned) ? scanned : []
}

function isScanRowComplete(item, scanNumber) {
  return getScannedBarcodes(item.scanKey).length >= scanNumber
}

function isScanItemComplete(item) {
  const requiredCount = getScanQuantityRows(item.quantity).length
  return requiredCount > 0 && getScannedBarcodes(item.scanKey).length >= requiredCount
}

function normalizeId(value) {
  if (value === undefined || value === null || value === '') return null
  return String(value)
}

async function handleHostedBarcodeScannerMessage(event) {
  if (event.origin !== HOSTED_BARCODE_SCANNER_ORIGIN || event.source !== hostedBarcodeScannerWindow) return
  if (event.data?.type !== 'barcode-scan-result' || !activeBarcodeScanKey) return

  const barcode = String(event.data.barcode || '').trim()
  hostedBarcodeScannerWindow?.close?.()
  hostedBarcodeScannerWindow = null
  activeBarcodeScanKey = null
  if (!barcode) return

  await validateAndRecordBarcode(barcode)
}

async function validateAndRecordBarcode(barcode) {
  const sessionId = scanModalSessionId
  if (!showScanModal.value) return

  const alreadyScanned = Object.values(scannedBarcodes.value)
    .flatMap((serials) => Array.isArray(serials) ? serials : [])
    .some((serial) => String(serial).toUpperCase() === barcode.toUpperCase())
  if (alreadyScanned) {
    showToast('Item already scanned', 'error')
    scanValue.value = ''
    return
  }
  if (isBarcodeValidationInProgress.value) return

  isBarcodeValidationInProgress.value = true
  scanValue.value = ''
  try {
    const params = new URLSearchParams({ serialNumber: barcode })
    const response = await apiFetch(`/orders/barcode-result?${params.toString()}`)
    const responseData = await response.json().catch(() => ({}))
    if (scanModalSessionId !== sessionId || !showScanModal.value) return
    if (!response.ok) {
      showToast(responseData.message || 'Failed to verify barcode.', 'error')
      return
    }

    const result = responseData.barcodeResult
    const resultItemId = normalizeId(result?.itemId ?? result?.item_id)
    const matchingItem = scanModalItems.value.find((candidate) => (
      normalizeId(candidate.itemId) === resultItemId && !isScanItemComplete(candidate)
    ))
    if (!matchingItem) {
      showToast('Wrong item', 'error')
      return
    }
    if (
      normalizeId(result.receiverCompanyId ?? result.receiver_company_id) !== normalizeId(companyId.value)
      || normalizeId(result.receiverBranchId ?? result.receiver_branch_id) !== normalizeId(branchId.value)
    ) {
      showToast('This is Not Yours', 'error')
      return
    }

    if (!departmentsLoaded.value && !(await loadAllDepartments())) {
      if (scanModalSessionId === sessionId && showScanModal.value) {
        showToast('Unable to load departments. Please try again.', 'error')
      }
      return
    }
    if (scanModalSessionId !== sessionId || !showScanModal.value) return

    const receiverDepartmentId = normalizeId(result.receiverDepartmentId ?? result.receiver_department_id)
    const receiverDepartmentExists = receiverDepartmentId != null
      && allDepartments.value.some((department) => normalizeId(department?.id) === receiverDepartmentId)
    if (!receiverDepartmentExists || !userDepartmentIds.value.has(receiverDepartmentId)) {
      showToast('This item is not given to the department you are assigned.', 'error')
      return
    }
    if (String(result.status || '').trim().toLowerCase() === 'used') {
      showToast('This item is already used', 'error')
      return
    }

    scannedBarcodes.value = {
      ...scannedBarcodes.value,
      [matchingItem.scanKey]: [...getScannedBarcodes(matchingItem.scanKey), barcode],
    }
  } catch (error) {
    console.error('Failed to verify scanned barcode:', error)
    if (scanModalSessionId === sessionId && showScanModal.value) {
      showToast('Unable to verify barcode. Please try again.', 'error')
    }
  } finally {
    isBarcodeValidationInProgress.value = false
    nextTick(() => scanInput.value?.focus())
  }
}

function openHostedBarcodeScanner(item) {
  if (!item || hostedBarcodeScannerWindow && !hostedBarcodeScannerWindow.closed) {
    hostedBarcodeScannerWindow?.focus?.()
    return
  }
  if (isScanItemComplete(item) || isBarcodeValidationInProgress.value) return

  const returnOrigin = window.location.origin
  if (!/^https?:$/.test(window.location.protocol) || !returnOrigin) {
    showToast('The hosted barcode scanner needs this page opened over HTTP.', 'error')
    return
  }

  const params = new URLSearchParams({ returnOrigin })
  activeBarcodeScanKey = item.scanKey
  hostedBarcodeScannerWindow = window.open(
    `${HOSTED_BARCODE_SCANNER_ORIGIN}/barcode-scanner?${params.toString()}`,
    'hostedBarcodeScanner',
    'popup=yes,width=520,height=760,resizable=yes,scrollbars=yes',
  )
  if (!hostedBarcodeScannerWindow) {
    activeBarcodeScanKey = null
    showToast('Please allow the barcode scanner popup.', 'error')
  }
}

async function openScanModal(order) {
  scanModalSessionId += 1
  scanModalOrder.value = order
  scanValue.value = ''
  scannedBarcodes.value = {}
  isBarcodeValidationInProgress.value = false
  showScanModal.value = true
  await nextTick()
  scanInput.value?.focus()
}

function closeScanModal() {
  if (isCompletingScannedOrderOut.value) return
  scanModalSessionId += 1
  showScanModal.value = false
  scanModalOrder.value = null
  scanValue.value = ''
  hostedBarcodeScannerWindow?.close?.()
  hostedBarcodeScannerWindow = null
  activeBarcodeScanKey = null
  isBarcodeValidationInProgress.value = false
}

async function outScannedOrder() {
  if (
    !scanModalOrder.value
    || !allScannedItemsComplete.value
    || isBarcodeValidationInProgress.value
    || isCompletingScannedOrderOut.value
  ) return
  if (!canOut.value) {
    showToast('You need Out permission to complete this scanned order.', 'error')
    return
  }

  const order = scanModalOrder.value
  const quantity = Number(order.quantity)
  if (!Number.isFinite(quantity) || quantity <= 0) {
    showToast('The selected order quantity is invalid.', 'error')
    return
  }
  const barcodeResults = scanModalItems.value.flatMap((item) => (
    getScannedBarcodes(item.scanKey).map((serialNumber) => ({
      serialNumber,
      itemId: item.itemId,
    }))
  ))
  const expectedBarcodeCount = scanModalItems.value.reduce(
    (total, item) => total + getScanQuantityRows(item.quantity).length,
    0,
  )
  if (barcodeResults.length !== expectedBarcodeCount || barcodeResults.length === 0) {
    showToast('Scan all required barcodes before moving this order out.', 'error')
    return
  }

  isCompletingScannedOrderOut.value = true
  try {
    const response = await apiFetch(`/orders/${order.id}/preparation/out`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity, barcodeResults }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showToast(data.message || 'Failed to move out', 'error')
      return
    }
    applyPreparationUpdate(order.id, data, true)
    showToast(
      data.message || 'Order moved out successfully',
      data.message?.toLowerCase().includes('sync failed') ? 'error' : 'success',
    )
    isCompletingScannedOrderOut.value = false
    closeScanModal()
  } catch (error) {
    console.error('Scanned order out error:', error)
    showToast('An error occurred while moving the scanned order out.', 'error')
  } finally {
    isCompletingScannedOrderOut.value = false
  }
}

function statusClass(status) {
  if (status === 'Pending') return 'text-orange-600'
  if (status === 'Started') return 'text-blue-600'
  if (status === 'Finished') return 'text-emerald-600'
  return 'text-slate-500'
}

function showToast(message, type = 'success') {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

function applyPreparationUpdate(orderId, data, isOut = false) {
  const idx = orders.value.findIndex(o => o.id === orderId)
  if (idx === -1) return
  if (data.statusCount) {
    orders.value[idx].statusCount = data.statusCount
  }
  if (data.preparation_status) {
    orders.value[idx].preparation_status = data.preparation_status
  }
  if (isOut) {
    orders.value[idx].outed_by_user_id = Number(currentUser.value?.id || currentUser.value?.sub || 0) || null
    orders.value[idx].outed_by_user_name = currentUser.value?.full_name || currentUser.value?.username || null
  }
}

async function handleAction(order) {
  if (!order?.id) return

  const actionQty = parseFloat(order.quantity) || 0
  const payload = JSON.stringify({ quantity: actionQty })

  if (order.preparation_status === 'Pending') {
    try {
      const res = await apiFetch(`/orders/${order.id}/preparation/start`, {
        method: 'PATCH',
        body: payload
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showToast(data.message || 'Failed to start preparation', 'error')
        return
      }

      applyPreparationUpdate(order.id, data)
      showToast(data.message || 'Preparation started successfully', 'success')
    } catch (err) {
      console.error('Start preparation error:', err)
      showToast('An error occurred while starting preparation', 'error')
    }
    return
  }

  if (order.preparation_status === 'Started') {
    try {
      const res = await apiFetch(`/orders/${order.id}/preparation/finish`, {
        method: 'PATCH',
        body: payload
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showToast(data.message || 'Failed to finish preparation', 'error')
        return
      }

      applyPreparationUpdate(order.id, data)
      showToast(data.message || 'Preparation finished successfully', 'success')
    } catch (err) {
      console.error('Finish preparation error:', err)
      showToast('An error occurred while finishing preparation', 'error')
    }
    return
  }

  if (order.preparation_status === 'Finished') {
    try {
      const res = await apiFetch(`/orders/${order.id}/preparation/out`, {
        method: 'PATCH',
        body: payload
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showToast(data.message || 'Failed to move out', 'error')
        return
      }

      applyPreparationUpdate(order.id, data, true)
      showToast(data.message || 'Order moved out successfully', 'success')
    } catch (err) {
      console.error('Out preparation error:', err)
      showToast('An error occurred while moving out', 'error')
    }
  }
}

async function loadOrders() {
  if(loadingCount.value === 0) {
    loading.value = true
  }

  try {
    const params = new URLSearchParams()
    // Required scoping: use branchId if present, otherwise companyId
    if (branchId.value !== null && branchId.value !== undefined && String(branchId.value) !== '') {
      params.append('branchId', branchId.value)
    } else if (companyId.value !== null && companyId.value !== undefined && String(companyId.value) !== '') {
      params.append('companyId', companyId.value)
    }
    const today = new Date().toISOString().split('T')[0]
    params.append('startDate', today)
    params.append('endDate', today)

    const qs = params.toString()
    const res = await apiFetch(`/orders${qs ? `?${qs}` : ''}`)

    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load orders', 'error')
      orders.value = []
      return
    }

    const [data, ingredientsRes] = await Promise.all([
      res.json(),
      apiFetch(`/orders/display-scannable-ingredients${qs ? `?${qs}` : ''}`),
    ])
    if (!ingredientsRes.ok) {
      const err = await ingredientsRes.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to load scannable item details')
    }
    const ingredientsData = await ingredientsRes.json()
    const ingredientsByOrderId = new Map(
      (Array.isArray(ingredientsData) ? ingredientsData : [])
        .map((entry) => [String(entry.orderId), entry.ingredient_and_amount]),
    )
    const currentOrders = (Array.isArray(data) ? data : []).map((order) => ({
      ...order,
      _scannableIngredientsKnown: ingredientsByOrderId.has(String(order.id)),
      ingredient_and_amount: ingredientsByOrderId.get(String(order.id)) ?? order.ingredientAndAmount ?? [],
    }))
    scannableIngredientsLoaded.value = true
    
    // Check for new relevant orders to announce
    if (isSpeechSupported() && currentOrders.length > 0) {
      const allowedDepts = userDepartmentIds.value
      const newOrders = currentOrders.filter(o => {
        const id = String(o.id)
        const deptId = String(o?.menu?.departmentId ?? o?.menu?.department_id)
        return allowedDepts.has(deptId) && !announcedOrderIds.value.has(id)
      })

      if (newOrders.length > 0) {
        // If it's not the first load of the page, or if we have at least one new order since last sync
        if (loadingCount.value > 0) {
          speak("New order is available")
        }
        
        // Mark these as announced so we don't speak again
        newOrders.forEach(o => announcedOrderIds.value.add(String(o.id)))
        saveAnnouncedIds()
      }
    }

    orders.value = currentOrders
  } catch (err) {
    console.error('Failed to load display orders:', err)
    if(loadingCount.value===0)
    showToast(err?.message || 'An error occurred while loading orders', 'error')
    orders.value = []
    scannableIngredientsLoaded.value = false
  } finally {
    loading.value = false
  }

    loadingCount.value++
}

async function loadUsers() {
  try {
    let url = ''
    const hasBranchId = branchId.value !== null && branchId.value !== undefined && String(branchId.value) !== ''
    const hasCompanyId = companyId.value !== null && companyId.value !== undefined && String(companyId.value) !== ''
    if (hasBranchId) {
      url = `/users/branch/${branchId.value}`
    } else if (hasCompanyId) {
      url = `/users/company/${companyId.value}`
    }
    if (!url) return

    const res = await apiFetch(url)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      users.value = Array.isArray(data) ? data : []
    }
  } catch (err) {
    console.error('Failed to load users for display page:', err)
    users.value = []
  }
}

async function loadAllDepartments() {
  try {
    let url = ''
    if (branchId.value) {
      url = `departments/branch/${branchId.value}`
    } else if (companyId.value) {
      url = `departments/company/${companyId.value}`
    }
    if (!url) return

    const res = await apiFetch(url)
    if (!res.ok) return false
    const departments = await res.json()
    if (!Array.isArray(departments)) return false
    allDepartments.value = departments
    departmentsLoaded.value = true
    return true
  } catch (error) {
    console.error('Failed to load departments map:', error)
    return false
  }
}

onMounted(() => {
  window.addEventListener('message', handleHostedBarcodeScannerMessage)
  loadUsers()
  loadAllDepartments()
  loadAnnouncedIds()
  loadSpeechVoices()
  if (typeof window !== 'undefined' && 'speechSynthesis' in window && typeof window.speechSynthesis.onvoiceschanged !== 'undefined') {
    window.speechSynthesis.onvoiceschanged = loadSpeechVoices
  }
  loadOrders()

  if (isSpeechSupported()) {
    window.addEventListener('click', unlockSpeech)
    window.addEventListener('touchstart', unlockSpeech)
  }
})

onUnmounted(() => {
  window.removeEventListener('message', handleHostedBarcodeScannerMessage)
  hostedBarcodeScannerWindow?.close?.()
  hostedBarcodeScannerWindow = null
  activeBarcodeScanKey = null
  window.removeEventListener('click', unlockSpeech)
  window.removeEventListener('touchstart', unlockSpeech)
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel()
  }
})

setInterval(() => {
  loadOrders()
}, 5000);
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 12px;
  line-height: 1.2;
  color: #1e293b;
}

th,
td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 8px 12px;
}

th {
  background: #f8fafc;
  cursor: default;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.025em;
}

.display-orders-table tbody tr.display-order-row {
  transition: background-color 0.15s ease;
}

.display-orders-table tbody tr.display-order-row:hover {
  background-color: #e2e8f0;
}

.display-orders-table tbody tr.display-order-row.table-group-end td {
  border-bottom: 1px solid #0d4169;
}

/* For better form control appearance */
select,
input {
  font-family: inherit;
}

.toast-enter-active, .toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from, .toast-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
